import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { useMarketSocket } from "@/hooks/use-market-socket";
import { apiGet, apiPost } from "@/lib/api-client";
import { asContractPrice, asCurrency, asFiniteNumber, formatRelativeTime } from "@/lib/format";
import { asRecord } from "@/lib/type-utils";
import { useLiveLedger, usePortfolioSummary } from "@/sdk/hooks/use-portfolio";
import type {
  LiveLedgerEdgeEvidence,
  LiveLedgerFill,
  LiveLedgerOrder,
} from "@/sdk/hooks/use-portfolio";
import {
  interpretWalletBalance,
  useLiveOpenOrders,
  useTradingBalance,
} from "@/sdk/hooks/use-trading";
import type { LiveOpenOrder } from "@/sdk/hooks/use-trading";

function asBool(input: unknown): boolean {
  return typeof input === "boolean" ? input : false;
}

function redactIdentifier(input: unknown): string {
  const raw = String(input ?? "").trim();
  if (!raw) return "unknown";
  if (raw.length <= 14) return raw;
  return `${raw.slice(0, 6)}...${raw.slice(-4)}`;
}

function sideBadgeClass(side: string): string {
  if (side === "BUY") return "bg-emerald-500/20 text-emerald-300";
  if (side === "SELL") return "bg-red-500/20 text-red-300";
  return "bg-white/10 text-white/55";
}

function openOrderId(order: LiveOpenOrder): string {
  return redactIdentifier(
    order.venue_order_id_redacted ??
      order.id ??
      order.order_id ??
      order.orderID ??
      order.hash,
  );
}

function openOrderMarket(order: LiveOpenOrder): string {
  const value = order.market_id ?? order.market ?? order.token_id ?? order.asset_id;
  const raw = String(value ?? "").trim();
  return raw || "market unknown";
}

function openOrderSize(order: LiveOpenOrder): number | null {
  const directSize = asFiniteNumber(order.size);
  if (directSize !== null) return directSize;

  const originalSize = asFiniteNumber(order.original_size);
  const matchedSize = asFiniteNumber(order.size_matched);
  if (originalSize === null) return null;
  if (matchedSize === null) return originalSize;
  return Math.max(originalSize - matchedSize, 0);
}

function openOrderKey(order: LiveOpenOrder, index: number): string {
  return String(order.id ?? order.order_id ?? order.orderID ?? order.hash ?? index);
}

function LiveOpenOrderRow({ order }: { order: LiveOpenOrder }) {
  const price = asFiniteNumber(order.price);
  const size = openOrderSize(order);
  const notional = price !== null && size !== null ? price * size : null;
  const status = String(order.status || order.state || "OPEN");
  const rawSide = typeof order.side === "string" ? order.side.trim() : "";
  const side = rawSide ? rawSide.toUpperCase() : "-";

  return (
    <div className="grid grid-cols-[1fr_4rem_5rem_5rem_6rem] items-center gap-3 rounded border border-white/5 bg-white/[0.02] px-3 py-2">
      <div className="min-w-0">
        <div className="truncate font-mono text-xs text-white/80">
          {openOrderId(order)}
        </div>
        <div className="truncate text-[11px] text-white/35">
          {openOrderMarket(order)}
        </div>
      </div>
      <span className={`rounded px-1.5 py-0.5 text-center text-[11px] font-semibold ${sideBadgeClass(side)}`}
      >
        {side}
      </span>
      <div className="text-right font-mono text-xs text-white/70">
        {price === null ? "-" : asContractPrice(price)}
      </div>
      <div className="text-right font-mono text-xs text-white/70">
        {size === null ? "-" : size.toFixed(2)}
      </div>
      <div className="text-right">
        <span className="rounded border border-blue-500/20 bg-blue-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-blue-300">
          {status}
        </span>
        <div className="mt-0.5 font-mono text-[10px] text-white/35">
          {notional === null ? "" : asCurrency(notional)}
        </div>
      </div>
    </div>
  );
}

const LEDGER_EXCEPTION_STATES = new Set([
  "REJECTED",
  "FAILED",
  "RECONCILE_REQUIRED",
  "CANCELLED",
  "EXPIRED",
]);
const LIVE_LEDGER_FILL_DISPLAY_LIMIT = 10;
const LIVE_LEDGER_EXCEPTION_DISPLAY_LIMIT = 5;

function usableRedactedId(value?: string): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed || trimmed === "...") {
    return null;
  }
  return trimmed;
}

function ledgerOrderId(order: LiveLedgerOrder): string {
  return usableRedactedId(order.venue_order_id_redacted) || redactIdentifier(order.order_key);
}

function liveLedgerFillKey(fill: LiveLedgerFill, index: number): string {
  const fillId = String(fill.fill_id || "").trim();
  if (fillId) {
    return `fill:${fillId}`;
  }
  return `fill:${fill.order_key || "missing"}:${index}`;
}

function liveLedgerExceptionKey(order: LiveLedgerOrder, index: number): string {
  const orderKey = String(order.order_key || "").trim();
  if (orderKey) {
    return `exception:${orderKey}`;
  }
  return `exception:${index}`;
}

function ledgerExceptionTone(state: string): string {
  if (state === "REJECTED" || state === "FAILED" || state === "RECONCILE_REQUIRED") {
    return "border-red-500/40 bg-red-500/10 text-red-300";
  }
  return "border-amber-500/40 bg-amber-500/10 text-amber-300";
}

function liveLedgerExceptionEventTime(order: LiveLedgerOrder): string | undefined {
  const state = String(order.state || "").toUpperCase();
  if (state === "RECONCILE_REQUIRED") {
    return order.updated_at || order.terminal_at || order.acked_at || order.submitted_at;
  }
  return order.terminal_at || order.updated_at || order.acked_at || order.submitted_at;
}

function LiveLedgerFillRow({ fill }: { fill: LiveLedgerFill }) {
  const rawSide = typeof fill.side === "string" ? fill.side.trim() : "";
  const side = rawSide ? rawSide.toUpperCase() : "-";
  const fillSize = asFiniteNumber(fill.fill_size);
  const feePaid = asFiniteNumber(fill.fee_paid);
  const fillTime = fill.venue_filled_at || fill.ingested_at;
  const fillId = String(fill.fill_id || "").trim();
  const displayFillId = fillId || usableRedactedId(fill.venue_order_id_redacted) || redactIdentifier(fill.order_key);

  return (
    <div className="grid grid-cols-[1fr_4rem_5rem_5rem_5rem_6rem] items-center gap-3 rounded border border-white/5 bg-white/[0.02] px-3 py-2">
      <div className="min-w-0">
        <div className="truncate font-mono text-xs text-white/80">
          {displayFillId}
        </div>
        <div className="truncate text-[11px] text-white/35">
          {fill.market_id || "-"}
        </div>
        {fillTime ? (
          <div className="truncate text-[10px] text-white/30">{formatRelativeTime(fillTime)}</div>
        ) : null}
      </div>
      <span className={`rounded px-1.5 py-0.5 text-center text-[11px] font-semibold ${sideBadgeClass(side)}`}>
        {side}
      </span>
      <div className="text-right font-mono text-xs text-white/70">
        {fillSize === null ? "-" : fillSize.toFixed(2)}
      </div>
      <div className="text-right font-mono text-xs text-white/70">
        {asContractPrice(fill.fill_price)}
      </div>
      <div className="text-right font-mono text-xs text-white/70">
        {feePaid === null ? "n/a" : asCurrency(feePaid)}
      </div>
      <div className="text-right">
        <span className="rounded border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-300">
          {fill.source || "fill"}
        </span>
      </div>
    </div>
  );
}

function LiveLedgerExceptionRow({ order }: { order: LiveLedgerOrder }) {
  const rawSide = typeof order.side === "string" ? order.side.trim() : "";
  const side = rawSide ? rawSide.toUpperCase() : "-";
  const state = String(order.state || "UNKNOWN").toUpperCase();
  const tone = ledgerExceptionTone(state);
  const eventTime = liveLedgerExceptionEventTime(order);
  const requestedNotional = asFiniteNumber(order.requested_notional);

  return (
    <div className="grid grid-cols-[1fr_4rem_6rem_6rem_7rem] items-center gap-3 rounded border border-white/5 bg-white/[0.02] px-3 py-2">
      <div className="min-w-0">
        <div className="truncate font-mono text-xs text-white/80">
          {ledgerOrderId(order)}
        </div>
        <div className="truncate text-[11px] text-white/35">
          {order.market_id || "-"}
        </div>
        {eventTime ? (
          <div className="truncate text-[10px] text-white/30">{formatRelativeTime(eventTime)}</div>
        ) : null}
      </div>
      <span className={`rounded px-1.5 py-0.5 text-center text-[11px] font-semibold ${sideBadgeClass(side)}`}>
        {side}
      </span>
      <div className="text-right font-mono text-xs text-white/70">
        {requestedNotional === null ? "n/a" : asCurrency(requestedNotional)}
      </div>
      <div className="text-right font-mono text-xs text-white/70">
        {asFiniteNumber(order.filled_size)?.toFixed(2) ?? "-"}
      </div>
      <div className="text-right">
        <span className={`rounded border px-1.5 py-0.5 text-[11px] font-semibold ${tone}`}>
          {state}
        </span>
      </div>
    </div>
  );
}

function NoOrderGuardEvidence({ evidence }: { evidence: LiveLedgerEdgeEvidence }) {
  const blocked = evidence.no_order_guard_blocked === true;
  const reasonCodes = (Array.isArray(evidence.reason_codes) ? evidence.reason_codes : [])
    .filter((code) => code.trim().length > 0);
  const reason = evidence.no_order_reason || reasonCodes.join(", ") || "No reason recorded";
  const tone = blocked
    ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-white/5 bg-white/[0.02] px-3 py-2">
        <div className="min-w-0">
          <div className="text-xs font-medium text-muted">Latest edge evidence</div>
          <div className="mt-0.5 truncate font-mono text-xs text-white/75">
            {evidence.correlation_id ? redactIdentifier(evidence.correlation_id) : "correlation unknown"}
          </div>
          {evidence.received_at ? (
            <div className="mt-0.5 text-[10px] text-white/35">
              {formatRelativeTime(evidence.received_at)}
            </div>
          ) : null}
        </div>
        <span className={`rounded border px-2 py-1 text-xs font-semibold ${tone}`}>
          {blocked ? "Blocked before submit" : "Not blocked"}
        </span>
      </div>

      <div className="rounded border border-white/5 bg-white/[0.02] px-3 py-2">
        <div className="text-xs font-medium text-muted">Reason</div>
        <div className="mt-1 text-sm text-white/80">{reason}</div>
        {reasonCodes.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {reasonCodes.map((code) => (
              <span
                key={code}
                className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px] text-white/55"
              >
                {code}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
        <div className="rounded border border-white/5 bg-white/[0.02] px-3 py-2">
          <div className="text-muted">Submitted</div>
          <div className="mt-1 font-mono text-white/75">{evidence.orders_submitted_count}</div>
        </div>
        <div className="rounded border border-white/5 bg-white/[0.02] px-3 py-2">
          <div className="text-muted">Open Orders</div>
          <div className="mt-1 font-mono text-white/75">{evidence.final_open_orders}</div>
        </div>
        <div className="rounded border border-white/5 bg-white/[0.02] px-3 py-2">
          <div className="text-muted">Geoblock</div>
          <div className="mt-1 truncate font-mono text-white/75">{evidence.geoblock_state || "unknown"}</div>
        </div>
        <div className="rounded border border-white/5 bg-white/[0.02] px-3 py-2">
          <div className="text-muted">Region</div>
          <div className="mt-1 truncate font-mono text-white/75">{evidence.region || "unknown"}</div>
        </div>
      </div>
    </div>
  );
}

export function CommandCenterPage() {
  const [assetInput, setAssetInput] = useState("");
  const [assetIds, setAssetIds] = useState<string[]>([]);

  const botStatusQuery = useQuery({
    queryKey: ["command-center", "bot-status"],
    queryFn: () => apiGet<unknown>("/api/bot/status"),
    refetchInterval: 15_000,
  });
  const signalsHealthQuery = useQuery({
    queryKey: ["command-center", "signals-health"],
    queryFn: () => apiGet<unknown>("/api/signals/health"),
    refetchInterval: 15_000,
  });
  const deepHealthQuery = useQuery({
    queryKey: ["command-center", "health-deep"],
    queryFn: () => apiGet<unknown>("/api/health/deep"),
    refetchInterval: 20_000,
  });
  const balanceQuery = useTradingBalance(30_000);
  const portfolioSummaryQuery = usePortfolioSummary();
  const liveLedgerQuery = useLiveLedger(10);
  const liveOpenOrdersQuery = useLiveOpenOrders();

  const connectLiveFeedMutation = useMutation({
    mutationFn: () => apiPost("/api/trading/live/connect"),
  });

  const liveCryptoQuery = useQuery({
    queryKey: ["command-center", "live-crypto"],
    queryFn: () => apiGet<unknown>("/api/trading/live/crypto"),
    enabled: connectLiveFeedMutation.isSuccess,
    refetchInterval: 5_000,
  });

  const socket = useMarketSocket({ assetIds, enabled: assetIds.length > 0 });

  const botStatus = asRecord(botStatusQuery.data);
  const deepHealth = asRecord(deepHealthQuery.data);
  const signalsHealth = asRecord(signalsHealthQuery.data);
  const portfolioSummary = portfolioSummaryQuery.data;
  const liveLedger = liveLedgerQuery.data;
  const liveLedgerCounts = liveLedger?.counts;
  const activeBaseline = [
    portfolioSummary?.baseline,
    liveLedger?.baseline,
  ].find((baseline) => baseline?.active);
  const liveBaselineHint = activeBaseline ? "Since go-live baseline" : undefined;
  const liveOpenOrders = liveOpenOrdersQuery.data?.orders ?? [];
  const liveLedgerFillCount = liveLedger?.fills.length ?? 0;
  const liveLedgerFills = useMemo(
    () => (liveLedger?.fills ?? []).slice(0, LIVE_LEDGER_FILL_DISPLAY_LIMIT),
    [liveLedger?.fills],
  );
  const liveLedgerExceptionOrders = useMemo(
    () => {
      const eventTime = (order: LiveLedgerOrder) =>
        Date.parse(liveLedgerExceptionEventTime(order) || "") || 0;

      return (liveLedger?.orders ?? [])
        .filter((order) => LEDGER_EXCEPTION_STATES.has(String(order.state || "").toUpperCase()))
        .sort((a, b) => eventTime(b) - eventTime(a));
    },
    [liveLedger?.orders],
  );
  const liveLedgerExceptionCount = liveLedgerExceptionOrders.length;
  const liveLedgerExceptions = liveLedgerExceptionOrders.slice(0, LIVE_LEDGER_EXCEPTION_DISPLAY_LIMIT);
  const latestEdgeEvidence = liveLedger?.edge_evidence?.latest ?? null;
  const liveLedgerFillsDescription =
    liveLedgerFillCount > liveLedgerFills.length
      ? `Showing ${liveLedgerFills.length} of ${liveLedgerFillCount} recent live fills`
      : `${liveLedgerFillCount} recent live fill${liveLedgerFillCount !== 1 ? "s" : ""}`;
  const liveLedgerExceptionsDescription =
    liveLedgerExceptionCount > liveLedgerExceptions.length
      ? `Showing ${liveLedgerExceptions.length} of ${liveLedgerExceptionCount} rejected or terminal-risk orders`
      : `${liveLedgerExceptionCount} rejected or terminal-risk order${liveLedgerExceptionCount !== 1 ? "s" : ""}`;
  const wallet = interpretWalletBalance(balanceQuery.data, {
    isError: balanceQuery.isError,
    isLoading: balanceQuery.isPending,
  });

  const socketStatusTone =
    socket.state === "connected"
      ? "success"
      : socket.state === "connecting" || socket.state === "reconnecting"
        ? "warning"
        : "default";

  const normalizedAssetIds = useMemo(
    () =>
      assetInput
        .split(",")
        .map((assetId) => assetId.trim())
        .filter((assetId) => assetId.length > 0),
    [assetInput],
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel
        title="Command Center"
        description="Operational control panel for bot orchestration, deep health, and realtime streams."
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          {/* A failed status read must say "Unknown" (like the Signals/Deep
              Health cards), never a definitive "No" — a dead backend is not
              evidence the bot is stopped. */}
          <StatCard
            label="Bot Running"
            value={
              botStatusQuery.isError
                ? "Unknown"
                : asBool(botStatus.running || botStatus.is_running)
                  ? "Yes"
                  : "No"
            }
            tone={
              botStatusQuery.isError
                ? "default"
                : asBool(botStatus.running || botStatus.is_running)
                  ? "success"
                  : "warning"
            }
            hint={botStatusQuery.isError ? "Status read failed" : undefined}
          />
          <StatCard
            label="Signals Health"
            value={signalsHealth.status ? String(signalsHealth.status) : "Unknown"}
            hint="From /api/signals/health"
          />
          <StatCard
            label="Deep Health"
            value={deepHealth.status ? String(deepHealth.status) : "Unknown"}
            hint="From /api/health/deep"
          />
          <StatCard
            label="Socket State"
            value={socket.state}
            tone={socketStatusTone}
            hint={assetIds.length > 0 ? `${assetIds.length} asset subscriptions` : "No asset subscriptions"}
          />
          <StatCard
            label={wallet.status === "available" ? `${wallet.label ?? "Wallet"} Balance` : "Wallet Balance"}
            value={
              wallet.status === "available"
                ? asCurrency(wallet.usdc)
                : wallet.status === "loading"
                  ? "..."
                  : "Unavailable"
            }
            tone={
              wallet.status === "unavailable" ||
              (wallet.status === "available" && wallet.stale === true)
                ? "warning"
                : undefined
            }
            hint={
              wallet.status === "available"
                ? wallet.stale === true
                  ? // Edge-relayed value aged past TTL — flag it, never show
                    // an hours-old read as a current balance.
                    `Stale — as of ${wallet.asOf ? formatRelativeTime(wallet.asOf) : "unknown time"}`
                  : wallet.hint ?? "Polymarket USDC"
                : wallet.status === "loading"
                  ? "Checking..."
                  : wallet.reason === "not-configured"
                    ? "Trading not configured"
                    : "Can't reach Polymarket"
            }
          />
          <StatCard
            label="Live Orders"
            value={typeof liveLedgerCounts?.orders === "number" ? String(liveLedgerCounts.orders) : "..."}
            hint={liveBaselineHint ?? "From live ledger"}
          />
          <StatCard
            label="Live Fills"
            value={typeof liveLedgerCounts?.fills === "number" ? String(liveLedgerCounts.fills) : "..."}
            hint={liveBaselineHint ?? "From live ledger"}
          />
          <StatCard
            label="Live Positions"
            value={
              typeof portfolioSummary?.live_open_positions === "number"
                ? String(portfolioSummary.live_open_positions)
                : typeof liveLedgerCounts?.positions === "number"
                  ? String(liveLedgerCounts.positions)
                  : "..."
            }
            hint={liveBaselineHint ?? "Live account state"}
          />
          {/* No total_value fallback here: /api/portfolio/summary blends paper
              agent positions into total_value, which must never render under a
              "Live" label. Missing live_total_invested after the query settles
              means the live figure is unavailable — say so. */}
          <StatCard
            label="Live Position Value"
            value={
              typeof portfolioSummary?.live_total_invested === "number"
                ? asCurrency(portfolioSummary.live_total_invested)
                : portfolioSummaryQuery.isPending
                  ? "..."
                  : "Unavailable"
            }
            tone={
              typeof portfolioSummary?.live_total_invested === "number" ||
              portfolioSummaryQuery.isPending
                ? undefined
                : "warning"
            }
            hint={
              typeof portfolioSummary?.live_total_invested === "number"
                ? liveBaselineHint ?? "From portfolio summary"
                : portfolioSummaryQuery.isPending
                  ? "Checking..."
                  : "Live ledger summary unavailable"
            }
          />
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel
          title="Current Live Open Orders"
          description={`${liveOpenOrders.length} open live order${liveOpenOrders.length !== 1 ? "s" : ""}`}
        >
          {liveOpenOrdersQuery.isError ? (
            <p className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              Failed to fetch live open orders.
            </p>
          ) : liveOpenOrdersQuery.isPending ? (
            <p className="py-6 text-center text-sm text-muted">Loading live open orders...</p>
          ) : (
            <div className="space-y-3">
              {liveOpenOrdersQuery.data?.note ? (
                <p className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  {liveOpenOrdersQuery.data.note}
                </p>
              ) : null}
              {liveOpenOrders.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">
                  No live open orders reported by the endpoint. Confirm venue-read health before treating the empty result as authoritative.
                </p>
              ) : (
                <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
                  <div className="min-w-[34rem] space-y-1">
                    <div className="grid grid-cols-[1fr_4rem_5rem_5rem_6rem] items-center gap-3 px-3 py-1.5">
                      <span className="text-xs font-medium text-muted">Order</span>
                      <span className="text-center text-xs font-medium text-muted">Side</span>
                      <span className="text-right text-xs font-medium text-muted">Price</span>
                      <span className="text-right text-xs font-medium text-muted">Size</span>
                      <span className="text-right text-xs font-medium text-muted">Status</span>
                    </div>
                    {liveOpenOrders.map((order, index) => (
                      <LiveOpenOrderRow
                        key={openOrderKey(order, index)}
                        order={order}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Panel>

        <Panel
          title="Recent Live Fills"
          description={liveLedgerFillsDescription}
        >
          {liveLedgerQuery.isError ? (
            <p className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              Failed to fetch live fills.
            </p>
          ) : liveLedgerQuery.isPending ? (
            <p className="py-6 text-center text-sm text-muted">Loading live fills...</p>
          ) : (
            <div className="space-y-3">
              {liveLedger?.note ? (
                <p className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  {liveLedger.note}
                </p>
              ) : null}
              {liveLedgerFills.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">No recent live fills in the ledger.</p>
              ) : (
                <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
                  <div className="min-w-[40rem] space-y-1">
                    <div className="grid grid-cols-[1fr_4rem_5rem_5rem_5rem_6rem] items-center gap-3 px-3 py-1.5">
                      <span className="text-xs font-medium text-muted">Fill</span>
                      <span className="text-center text-xs font-medium text-muted">Side</span>
                      <span className="text-right text-xs font-medium text-muted">Size</span>
                      <span className="text-right text-xs font-medium text-muted">Price</span>
                      <span className="text-right text-xs font-medium text-muted">Fee</span>
                      <span className="text-right text-xs font-medium text-muted">Source</span>
                    </div>
                    {liveLedgerFills.map((fill, index) => (
                      <LiveLedgerFillRow key={liveLedgerFillKey(fill, index)} fill={fill} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Panel>

        <Panel
          title="Live Ledger Exceptions"
          description={liveLedgerExceptionsDescription}
        >
          {liveLedgerQuery.isError ? (
            <p className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              Failed to fetch live ledger exceptions.
            </p>
          ) : liveLedgerQuery.isPending ? (
            <p className="py-6 text-center text-sm text-muted">Loading live ledger exceptions...</p>
          ) : (
            <div className="space-y-3">
              {liveLedger?.note ? (
                <p className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  {liveLedger.note}
                </p>
              ) : null}
              {liveLedgerExceptions.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted">
                  No live ledger exceptions in the latest terminal-risk sample.
                </p>
              ) : (
                <div className="overflow-x-auto -mx-3 px-3 md:mx-0 md:px-0">
                  <div className="min-w-[36rem] space-y-1">
                    <div className="grid grid-cols-[1fr_4rem_6rem_6rem_7rem] items-center gap-3 px-3 py-1.5">
                      <span className="text-xs font-medium text-muted">Order</span>
                      <span className="text-center text-xs font-medium text-muted">Side</span>
                      <span className="text-right text-xs font-medium text-muted">Notional</span>
                      <span className="text-right text-xs font-medium text-muted">Filled</span>
                      <span className="text-right text-xs font-medium text-muted">State</span>
                    </div>
                    {liveLedgerExceptions.map((order, index) => (
                      <LiveLedgerExceptionRow key={liveLedgerExceptionKey(order, index)} order={order} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Panel>

        <Panel
          title="Latest No-Order Guard"
          description="Most recent Ireland edge no-order evidence."
        >
          {liveLedgerQuery.isError ? (
            <p className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              Failed to fetch Ireland edge evidence.
            </p>
          ) : liveLedgerQuery.isPending ? (
            <p className="py-6 text-center text-sm text-muted">Loading Ireland edge evidence...</p>
          ) : (
            <div className="space-y-3">
              {liveLedger?.edge_evidence?.note ? (
                <p className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  {liveLedger.edge_evidence.note}
                </p>
              ) : null}
              {latestEdgeEvidence ? (
                <NoOrderGuardEvidence evidence={latestEdgeEvidence} />
              ) : (
                <p className="py-6 text-center text-sm text-muted">
                  No Ireland edge evidence received yet.
                </p>
              )}
            </div>
          )}
        </Panel>

        <Panel
          title="Live Crypto Feed"
          description="Connect backend live feed endpoint and inspect streaming snapshots."
          actions={
            <Button
              onClick={() => connectLiveFeedMutation.mutate()}
              disabled={connectLiveFeedMutation.isPending}
            >
              {connectLiveFeedMutation.isPending ? "Connecting..." : "Connect Feed"}
            </Button>
          }
        >
          {connectLiveFeedMutation.error ? (
            <p className="mb-3 rounded-md bg-red-950/40 p-2 text-xs text-red-200">
              Failed to connect live feed.
            </p>
          ) : null}
          <JsonView
            value={liveCryptoQuery.data}
            emptyMessage="Connect live feed to start polling /api/trading/live/crypto."
          />
        </Panel>

        <Panel
          title="Polymarket WebSocket"
          description="Subscribe to asset IDs for low-latency order-book and price events."
        >
          <div className="mb-3">
            <Label>Comma-separated asset IDs</Label>
            <Input
              value={assetInput}
              onChange={(event) => setAssetInput(event.target.value)}
              placeholder="asset-1, asset-2, ..."
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setAssetIds(normalizedAssetIds)}
              disabled={normalizedAssetIds.length === 0}
            >
              Subscribe
            </Button>
            <Button variant="ghost" onClick={() => setAssetIds([])}>
              Disconnect
            </Button>
            <Button variant="ghost" onClick={socket.reconnect}>
              Reconnect
            </Button>
          </div>
          <div className="mt-3 text-xs text-muted">
            Latest state: <span className="font-semibold text-text">{socket.state}</span>
          </div>
          <JsonView value={socket.lastMessage} emptyMessage="No socket message received yet." />
        </Panel>
      </div>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Live Balance Payload" description="Raw /api/trading/balance response for command decisions.">
          <JsonView value={balanceQuery.data} emptyMessage="No live balance response received yet." />
        </Panel>
        <Panel title="Live Ledger Payload" description="Raw /api/portfolio/live-ledger response for command decisions.">
          <JsonView value={liveLedger} emptyMessage="No live ledger response received yet." />
        </Panel>
      </div>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Portfolio Summary" description="Live portfolio summary from /api/portfolio/summary.">
          <JsonView value={portfolioSummary} emptyMessage="No portfolio summary response received yet." />
        </Panel>
        <Panel title="Recent WebSocket Events" description="Last 30 inbound market messages.">
          <JsonView value={socket.messages} />
        </Panel>
      </div>
    </div>
  );
}
